import React, { useState, useEffect, useRef } from 'react'
import { Alert, Button, Input, Modal, Select, Checkbox, message, Tooltip, Spin } from 'antd'
import { TYPE_PARTNER, PARTNER_GROUP, SEWING_PARTNER, RECEIVER_FIELDS } from '../../../../helpers/typePartner'
import { updatePartner, listSku } from "../../../../services/api/3dApi"
import { canManagePartner, getNoPermissionMessage, getPartnerErrorMessage, DISABLED_ICON_STYLE } from '../../helpers/partnerPermission'
import ReceiverFields from './ReceiverFields'

const { Option } = Select

const EMPTY_GROUP = { name: '', staff_count: '' }

const getInitReceiver = (item = {}) => RECEIVER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: item[f.key] || '' }), {})

function EditPartnerModal(props) {
    const { item, reset } = props
    const {
        name: itemName,
        type_partner,
        has_groups,
        groups: itemGroups = [],
        partner_group: itemPartnerGroup,
        staff_count: itemStaffCount,
        productivity: itemProductivity,
        daily_quota: itemDailyQuota,
        production_skus: itemProductionSkus = [],
    } = item || {}

    const initGroups = itemGroups.length
        ? itemGroups.map(g => ({ _id: g._id, name: g.name, staff_count: String(g.staff_count ?? '') }))
        : [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }]

    const [showEditModal, setShowEditModal]     = useState(false)
    const [loading, setLoading]                 = useState(false)
    const [error, setError]                     = useState('')
    const [name, setName]                       = useState(itemName || '')
    const [type, setType]                       = useState(type_partner || '')
    const [hasGroups, setHasGroups]             = useState(has_groups || false)
    const [groups, setGroups]                   = useState(initGroups)
    const [listGroupDuplicates, setListGroupDuplicates] = useState([])
    const [partnerGroup, setPartnerGroup]       = useState(itemPartnerGroup || undefined)
    const [staffCount, setStaffCount]           = useState(String(itemStaffCount ?? ''))
    const [productivity, setProductivity]       = useState(String(itemProductivity ?? ''))
    const [dailyQuota, setDailyQuota]           = useState(String(itemDailyQuota ?? ''))
    const [productionSkus, setProductionSkus]   = useState(itemProductionSkus)
    const [receiver, setReceiver]               = useState(getInitReceiver(item || {}))
    const [skuOptions, setSkuOptions]           = useState([])
    const [skuLoading, setSkuLoading]           = useState(false)

    const inputRefs = useRef([])
    const skuRequestRef = useRef(0)

    const showModal = async () => {
        setShowEditModal(true)
        setLoading(false)
        setError('')
        setName(itemName || '')
        setType(type_partner || '')
        setHasGroups(has_groups || false)
        setGroups(
            itemGroups.length
                ? itemGroups.map(g => ({ _id: g._id, name: g.name, staff_count: String(g.staff_count ?? '') }))
                : [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }]
        )
        setListGroupDuplicates([])
        setPartnerGroup(itemPartnerGroup || undefined)
        setStaffCount(String(itemStaffCount ?? ''))
        setProductivity(String(itemProductivity ?? ''))
        setDailyQuota(String(itemDailyQuota ?? ''))
        setProductionSkus(itemProductionSkus || [])
        setReceiver(getInitReceiver(item || {}))

        // Load SKU options — request counter chống race condition
        const requestId = ++skuRequestRef.current
        setSkuLoading(true)
        try {
            const { data: res } = await listSku()
            if (requestId !== skuRequestRef.current) return
            if (res.success) {
                setSkuOptions(res.data.filter(i => !/\s/.test(i.sku)))
            }
        } catch (_) {
            // không block form
        } finally {
            if (requestId === skuRequestRef.current) setSkuLoading(false)
        }
    }

    const closeModal = () => {
        setShowEditModal(false)
    }

    const handleChangeGroupValue = (index, field, value) => {
        const newGroups = groups.map((g, idx) => idx === index ? { ...g, [field]: value } : g)
        const names = newGroups.map(g => g.name)
        const dupes = names.filter(Boolean).length > 1 ? findDuplicates(names) : (listGroupDuplicates.includes('') ? [''] : [])
        setGroups(newGroups)
        setListGroupDuplicates(dupes)
    }

    const handleAddGroup = () => {
        setGroups(prev => [...prev, { ...EMPTY_GROUP }])
        setTimeout(focusNewEmptyInput, 100)
    }

    const handleDeleteGroup = (index, deleteAll) => {
        setGroups(deleteAll ? [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }] : groups.filter((_, idx) => idx !== index))
        setListGroupDuplicates([])
        setError('')
    }

    const handleChangeCheckbox = (e) => {
        const checked = e.target.checked
        setHasGroups(checked)
        if (checked) {
            const newGroups = itemGroups.length
                ? itemGroups.map(g => ({ _id: g._id, name: g.name, staff_count: String(g.staff_count ?? '') }))
                : [{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }]
            setGroups(newGroups)
            setTimeout(() => {
                if (inputRefs.current && inputRefs.current[0]) inputRefs.current[0].focus()
            }, 100)
        } else {
            setGroups([{ ...EMPTY_GROUP }, { ...EMPTY_GROUP }])
        }
    }

    const findDuplicates = (arr) => {
        const seen = new Set()
        const duplicates = new Set()
        for (const item of arr) {
            const trimmed = item ? item.trim() : ''
            if (trimmed && seen.has(trimmed)) {
                duplicates.add(trimmed)
            } else {
                seen.add(trimmed)
            }
        }
        return Array.from(duplicates)
    }

    const focusNewEmptyInput = () => {
        const lastInput = inputRefs.current[inputRefs.current.length - 1]
        if (lastInput) lastInput.focus()
    }

    const handleSkuPaste = (e) => {
        const text = e.clipboardData && e.clipboardData.getData('text')
        if (!text) return
        const parsed = text.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
        if (parsed.length <= 1) return
        e.preventDefault()
        const validSkus = new Set(skuOptions.map(o => o.sku))
        const toAdd = parsed.filter(s => validSkus.has(s))
        const merged = [...new Set([...productionSkus, ...toAdd])]
        setProductionSkus(merged)
    }

    const validateNumbers = () => {
        if (!hasGroups && staffCount !== '' && !/^[1-9]\d*$/.test(staffCount)) {
            return 'Số lượng nhân sự phải là số nguyên dương'
        }
        if (productivity !== '' && (isNaN(parseFloat(productivity)) || parseFloat(productivity) < 0 || !/^\d+(\.\d+)?$/.test(productivity))) {
            return 'Năng suất phải là số thập phân >= 0'
        }
        if (dailyQuota !== '' && !/^\d+$/.test(dailyQuota)) {
            return 'Định mức theo số lượng phải là số nguyên dương lớn hơn hoặc bằng 0'
        }
        for (let i = 0; i < groups.length; i++) {
            const sc = groups[i].staff_count
            if (sc !== '' && !/^\d+$/.test(sc)) {
                return `Nhân sự nhóm sản xuất ${i + 1} phải là số nguyên dương lớn hơn hoặc bằng 0`
            }
        }
        return null
    }

    const totalStaff = groups.reduce((sum, g) => sum + (parseInt(g.staff_count) || 0), 0)

    const editPartner = async () => {
        if (!item?._id) return
        const numError = validateNumbers()
        if (numError) { setError(numError); return }

        setLoading(true)
        setError('')
        setListGroupDuplicates([])

        const newGroups = groups.map(g => ({ ...g, name: g.name.trim() })).filter(g => g.name)
        const newListGroupDuplicates = findDuplicates(newGroups.map(g => g.name))

        if (hasGroups && newGroups.length < 2) {
            setLoading(false)
            setError('Cần nhập ít nhất tên 2 nhóm sản xuất.')
            setGroups(groups.map(g => ({ ...g, name: g.name.trim() })))
            setListGroupDuplicates([''])
            return
        }

        if (hasGroups && newListGroupDuplicates.length > 0) {
            setLoading(false)
            setError('Tên nhóm sản xuất không được trùng nhau.')
            setListGroupDuplicates(newListGroupDuplicates)
            setGroups(groups.map(g => ({ ...g, name: g.name.trim() })))
            return
        }

        try {
            const payload = {
                name,
                type_partner: type,
                has_groups: hasGroups,
                partner_group: partnerGroup || null,
                production_skus: productionSkus,
                ...(!hasGroups && staffCount !== ''  && { staff_count: parseInt(staffCount) }),
                ...(productivity !== ''              && { productivity: parseFloat(productivity) }),
                ...(dailyQuota !== ''                && { daily_quota: parseInt(dailyQuota) }),
            }

            // Đối tác May luôn gửi đủ thông tin nhận hàng; chuỗi rỗng để xoá giá trị cũ
            if (type === SEWING_PARTNER) {
                RECEIVER_FIELDS.forEach(f => { payload[f.key] = receiver[f.key].trim() })
            }

            if (hasGroups && newGroups.length > 0) {
                payload.groups = newGroups.map(g => ({
                    ...(g._id && { _id: g._id }),
                    name: g.name,
                    ...(g.staff_count !== '' && { staff_count: parseInt(g.staff_count) }),
                }))
            }

            const { data } = await updatePartner(item._id, payload)
            const { success, error } = data

            if (!success) throw new Error(error)

            setShowEditModal(false)
            message.success('Chỉnh sửa đối tác thành công!')
            reset()
        } catch (e) {
            setError(getPartnerErrorMessage(e, 'sửa'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        inputRefs.current = groups.map((_, idx) => inputRefs.current[idx] || null)
    }, [groups])

    const canManage = canManagePartner()

    if (!canManage) {
        return (
            <div className='EditPartner Icon'>
                <Tooltip placement="top" title={getNoPermissionMessage('sửa')}>
                    <i className="fa fa-edit text-muted" style={DISABLED_ICON_STYLE} />
                </Tooltip>
            </div>
        )
    }

    return (
        <div className='EditPartner Icon cursor-pointer'>
            <Tooltip placement="top" title="Chỉnh sửa đối tác">
                <i className="fa fa-edit text-success" onClick={showModal} />
            </Tooltip>
            <Modal
                title="Chỉnh sửa đối tác"
                visible={showEditModal}
                className='EditPartnerModal'
                onOk={editPartner}
                onCancel={closeModal}
                maskClosable={false}
                width={640}
                footer={[
                    <Button key="back" onClick={closeModal}>Hủy</Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={editPartner}
                        disabled={loading || !name || !type}
                    >
                        Lưu
                    </Button>
                ]}
            >
                {error && <Alert className='mb-3' message={error} type='error' />}

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Tên đối tác <span className='text-danger'>*</span></label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder='Tên đối tác'
                            autoFocus
                            allowClear
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Loại đối tác <span className='text-danger'>*</span></label>
                        <Select
                            style={{ width: '100%' }}
                            value={type || undefined}
                            onChange={setType}
                            placeholder='Loại đối tác'
                            allowClear
                        >
                            {TYPE_PARTNER.map((item, index) => (
                                <Option key={index} value={item.value}>{item.name}</Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Nhóm đối tác</label>
                        <Select
                            style={{ width: '100%' }}
                            value={partnerGroup || undefined}
                            onChange={setPartnerGroup}
                            placeholder='Chọn nhóm đối tác'
                            allowClear
                        >
                            {PARTNER_GROUP.map((item, index) => (
                                <Option key={index} value={item.value}>{item.name}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Số lượng nhân sự (đối tác)</label>
                        {hasGroups ? (
                            <Input
                                disabled
                                value={totalStaff}
                                style={{ width: '100%', background: '#f5f5f5' }}
                            />
                        ) : (
                            <Input
                                value={staffCount}
                                placeholder='VD: 50'
                                style={{ width: '100%' }}
                                onChange={e => {
                                    const v = e.target.value
                                    if (v === '' || /^[1-9]\d*$/.test(v)) setStaffCount(v)
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Năng suất (%)</label>
                        <Input
                            value={productivity}
                            placeholder='VD: 85.5'
                            onChange={e => {
                                const v = e.target.value
                                if (v === '' || /^(0|[1-9]\d*)(\.\d*)?$/.test(v)) setProductivity(v)
                            }}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <label className='font-weight-bold'>Định mức theo số lượng (psc/day)</label>
                        <Input
                            value={dailyQuota}
                            placeholder='VD: 1000'
                            onChange={e => {
                                const v = e.target.value
                                if (v === '' || /^(0|[1-9]\d*)$/.test(v)) setDailyQuota(v)
                            }}
                        />
                    </div>
                </div>

                {/* SKU sản xuất */}
                <div className="mb-3">
                    <label className='font-weight-bold'>SKU sản xuất <span className='font-italic font-weight-normal'>(Nếu không chọn gì mặc định là Tất cả SKU)</span></label>
                    <Spin spinning={skuLoading}>
                        <div onPaste={handleSkuPaste}>
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                value={productionSkus}
                                onChange={setProductionSkus}
                                placeholder='Chọn hoặc dán danh sách SKU...'
                                allowClear
                                showArrow
                                filterOption={(input, option) =>
                                    option.props.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {skuOptions.map(o => (
                                    <Option key={o._id} value={o.sku}>{o.sku}</Option>
                                ))}
                            </Select>
                        </div>
                    </Spin>
                </div>

                {/* Nhóm sản xuất */}
                <div className="group">
                    <Checkbox checked={hasGroups} onChange={handleChangeCheckbox} className='font-weight-bold'>
                        Nhóm sản xuất
                    </Checkbox>
                    {hasGroups && (
                        <>
                            <div className="listGroup my-3">
                                <table>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th className='pl-3' style={{ minWidth: 220 }}>Tên nhóm</th>
                                            <th className='pl-3' style={{ width: 100 }}>Nhân sự</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map((group, idx) => (
                                            <tr key={`groupRow_${idx}`}>
                                                <td>
                                                    <span className='groupIndex'>Nhóm sản xuất {idx + 1}</span>
                                                </td>
                                                <td>
                                                    <Input
                                                        placeholder='Nhập tên nhóm sản xuất'
                                                        style={{ width: 200 }}
                                                        className={`inputGroup mx-3 my-1 ${listGroupDuplicates.includes(group.name.trim()) ? 'Error' : ''}`}
                                                        value={group.name}
                                                        allowClear
                                                        onChange={(e) => handleChangeGroupValue(idx, 'name', e.target.value)}
                                                        ref={el => inputRefs.current[idx] = el}
                                                    />
                                                </td>
                                                <td>
                                                    <Input
                                                        placeholder='0'
                                                        style={{ width: 80 }}
                                                        className='mx-3 my-1'
                                                        value={group.staff_count}
                                                        onChange={e => {
                                                            const v = e.target.value
                                                            if (v === '' || /^(0|[1-9]\d*)$/.test(v)) handleChangeGroupValue(idx, 'staff_count', v)
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    {idx > 0 && (
                                                        <Tooltip title='Xóa'>
                                                            <span>
                                                                <i
                                                                    className="fa fa-trash text-danger cursor-pointer"
                                                                    style={{ fontSize: 17 }}
                                                                    onClick={() => handleDeleteGroup(idx)}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex">
                                <Tooltip title='Thêm nhóm sản xuất'>
                                    <span className='text-primary mr-5 cursor-pointer' onClick={handleAddGroup}>
                                        <i className="fa fa-plus-circle mr-1" style={{ fontSize: 17 }} />
                                        Thêm nhóm sản xuất
                                    </span>
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>

                {type === SEWING_PARTNER && (
                    <div className='mt-3'>
                        <ReceiverFields
                            values={receiver}
                            onChange={(key, value) => setReceiver(prev => ({ ...prev, [key]: value }))}
                        />
                    </div>
                )}
            </Modal>
        </div>
    )
}

export default EditPartnerModal
